/*
  Accessibility behaviour that layers on top of the theme.

  assets/theme.js is a minified Prestige v4 vendor bundle with no readable
  source (its sourcemap contains pre-minified code), so it must not be edited.
  Everything here is additive and degrades safely if the markup changes.
*/
(function () {
  'use strict';

  /*
    Enter selects a size.

    A radio only responds to Space; Enter does nothing, and inside a form Enter
    would submit it. Keyboard users — and everyone who tests a page by pressing
    Tab then Enter — read that as a broken control. Selecting on Enter as well
    is additive: Space and the arrow keys keep their native behaviour.

    We call .click() rather than setting .checked so that the theme's own
    listeners (variant swap, price update, Klaviyo back-in-stock) fire exactly
    as they do for a mouse click.
  */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' || event.defaultPrevented) return;

    var target = event.target;
    if (!target || !target.classList || !target.classList.contains('SizeSwatch__Radio')) return;

    event.preventDefault(); // otherwise Enter submits the product form
    if (!target.checked) target.click();
  });

  /* ------------------------------------------------------------------------
     Focus management for drawers, popovers and modals.

     The theme signals "open" with aria-hidden="false" and ships no Escape
     handling at all (theme.js contains no keyboard close). Without a trap, Tab
     walks straight out of an open cart drawer into the page behind it, which is
     still scrollable and clickable — WCAG 2.1.2 No Keyboard Trap in reverse,
     and 2.4.3 Focus Order.

     Strategy: watch aria-hidden on the known containers. On open, remember what
     had focus, move focus inside, and cycle Tab within. On close, restore focus
     to the control that opened it. Escape activates the theme's own close
     control so its internal state stays consistent.
     ---------------------------------------------------------------------- */

  var MODAL_SELECTORS = [
    '#sidebar-cart', // cart drawer
    '#sidebar-menu', // mobile navigation
    '#Search', // search overlay
    '.NewsletterPopup',
    '.CollectionFilters', // filter drawers
    '.store-availabilities-modal',
  ];

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /*
    Modals that signal "open" with a class rather than aria-hidden. #GeoPopUp (the
    country selector) is display:none until .is-visible, has no close button, and
    is dismissed by clicking .PageOverlay — so Escape does the same thing, which
    keeps custom.js's own state (body scroll lock, localStorage) consistent.
  */
  var CLASS_MODALS = [
    {
      selector: '#GeoPopUp',
      openClass: 'is-visible',
      close: function () {
        var overlay = document.querySelector('.PageOverlay');
        if (overlay) overlay.click();
      },
    },
  ];

  var lastFocused = null;
  var activeModal = null;
  var activeClose = null;

  function visibleFocusable(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      var style = window.getComputedStyle(el);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        el.getAttribute('aria-hidden') !== 'true' &&
        el.offsetParent !== null
      );
    });
  }

  function closeModal(container) {
    if (activeClose) {
      activeClose();
      return;
    }

    var closer = container.querySelector(
      '[data-action="close-drawer"], [data-action="close-popup"],' +
        '[data-action="close-search"], [data-action="close-modal"], [data-action="close-popover"]',
    );
    if (closer) {
      closer.click();
    } else {
      container.setAttribute('aria-hidden', 'true');
    }
  }

  function onModalKeydown(event) {
    if (!activeModal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    if (event.key !== 'Tab') return;

    var focusable = visibleFocusable(activeModal);
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    // Focus may sit on the container itself (tabindex="-1"); treat that as "before first".
    if (event.shiftKey && (document.activeElement === first || !activeModal.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /*
    aria-modal="true" tells a screen reader the rest of the page is unavailable,
    but it does not make it so — the virtual cursor can still read behind an open
    dialog, and a Tab trap does nothing about that. A native <dialog>.showModal()
    would inert the background for us; these are plain divs, so do it explicitly.

    Only siblings outside the modal are inerted, never an ancestor of it.
  */
  /*
    TEMPORARY EMERGENCY HOTFIX (2026-07-17): background inert is disabled.

    This function used to stamp `inert` on every element outside an open modal.
    The Cookie Information banner is a body-level sibling, so it was inerted too,
    and its Accept/Decline controls stopped responding on a first visit (banner
    showing while the country selector auto-opens) — the whole screen looked
    frozen. Making this a no-op is the zero-risk stopgap: nothing can be left
    non-interactive, on any store, regardless of markup.

    Focus trapping and Escape handling in openModal/onModalKeydown still work.
    The proper fix (sequence the geo popup behind cookie consent, then re-enable
    a scoped inert that never touches the banner) replaces this.

    TO RE-ENABLE: delete the early `return;` below and uncomment the original
    implementation. Before doing so, add a skip for the cookie banner so it is
    never inerted, e.g. inside the loop:
      if (sibling.matches && sibling.matches('#cookie-information-template-wrapper')) continue;
      if (sibling.querySelector && sibling.querySelector('#cookie-information-template-wrapper')) continue;
  */
  function setBackgroundInert(container, inert) {
    return; // HOTFIX: inert disabled — see note above. Remove this line to re-enable.

    /* Original implementation, preserved for restoration:
    var node = container;
    while (node && node.parentElement) {
      var siblings = node.parentElement.children;
      for (var i = 0; i < siblings.length; i++) {
        var sibling = siblings[i];
        if (sibling === node || sibling.contains(container)) continue;
        if (inert) {
          sibling.setAttribute('inert', '');
        } else {
          sibling.removeAttribute('inert');
        }
      }
      node = node.parentElement;
      if (node === document.body) break;
    }
    */
  }

  function openModal(container, closeFn) {
    if (activeModal === container) return;

    lastFocused = document.activeElement;
    activeModal = container;
    activeClose = closeFn || null;
    setBackgroundInert(container, true);

    var focusable = visibleFocusable(container);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      container.setAttribute('tabindex', '-1');
      container.focus();
    }

    document.addEventListener('keydown', onModalKeydown, true);
  }

  function releaseModal() {
    if (!activeModal) return;

    document.removeEventListener('keydown', onModalKeydown, true);
    setBackgroundInert(activeModal, false);
    activeModal = null;
    activeClose = null;

    // Return focus to whatever opened it, so the user does not land at the top of the page.
    if (lastFocused && document.contains(lastFocused) && lastFocused.offsetParent !== null) {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /*
    The theme opens a drawer by flipping its aria-hidden, but never updates the
    aria-expanded on the trigger that opened it (the burger, the cart icon), so a
    screen reader is never told the drawer opened. The trigger is linked to the
    container by data-drawer-id === container.id; mirror the state onto it.
  */
  function syncOpenerExpanded(container, open) {
    if (!container.id) return;
    var openers = document.querySelectorAll('[data-action="open-drawer"][data-drawer-id="' + container.id + '"]');
    Array.prototype.forEach.call(openers, function (opener) {
      opener.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function watch(container) {
    var observer = new MutationObserver(function () {
      var open = container.getAttribute('aria-hidden') === 'false';
      syncOpenerExpanded(container, open);
      if (open) {
        openModal(container);
      } else if (activeModal === container) {
        releaseModal();
      }
    });

    observer.observe(container, { attributes: true, attributeFilter: ['aria-hidden'] });

    // Catch anything already open at parse time.
    if (container.getAttribute('aria-hidden') === 'false') openModal(container);
  }

  function watchClassModal(config) {
    var container = document.querySelector(config.selector);
    if (!container) return;

    var observer = new MutationObserver(function () {
      var open = container.classList.contains(config.openClass);
      if (open) {
        openModal(container, config.close);
      } else if (activeModal === container) {
        releaseModal();
      }
    });

    observer.observe(container, { attributes: true, attributeFilter: ['class'] });
    if (container.classList.contains(config.openClass)) openModal(container, config.close);
  }

  /*
    Make the skip link actually move focus to its target.

    <main> already carries tabindex="-1" so it can receive focus, but something in
    the vendor theme.js calls preventDefault() on the anchor's activation — the
    hash never changes and focus stays on the link, so the next Tab drops the user
    back into the header they just skipped (WCAG 2.4.1). Force the focus ourselves
    on click; this runs alongside whatever prevents the default navigation.
  */
  function wireSkipLink() {
    var link = document.querySelector('.PageSkipLink');
    if (!link) return;

    link.addEventListener('click', function (event) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;

      var target = document.getElementById(href.slice(1));
      if (!target) return;

      event.preventDefault();
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus();
      target.scrollIntoView();
    });
  }

  function init() {
    document.querySelectorAll(MODAL_SELECTORS.join(',')).forEach(function (el) {
      watch(el);
    });
    CLASS_MODALS.forEach(watchClassModal);
    wireSkipLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
